import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies for session-based guest cart
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        localStorage.setItem('accessToken', access);

        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/token/', { email, password }),
  verifyMFA: (temp_token: string, code: string) =>
    api.post('/token/mfa-verify/', { temp_token, code }),
  register: (data: any) => api.post('/auth/register/', data),
  getProfile: () => api.get('/auth/profile/'),
  updateProfile: (data: any) => api.patch('/auth/profile/', data),
  changePassword: (data: { current_password: string; new_password: string; confirm_password: string }) =>
    api.post('/auth/change-password/', data),
  // MFA endpoints
  getMFAStatus: () => api.get('/auth/mfa/status/'),
  setupMFA: () => api.post('/auth/mfa/setup/'),
  verifyMFASetup: (code: string) => api.post('/auth/mfa/verify-setup/', { code }),
  disableMFA: (code: string, password: string) => api.post('/auth/mfa/disable/', { code, password }),
  regenerateBackupCodes: (code: string) => api.post('/auth/mfa/regenerate-backup-codes/', { code }),
};

// Analytics API (Admin only)
export const analyticsAPI = {
  getSummary: () => api.get('/analytics/summary/'),
  getSessions: (params?: any) => api.get('/analytics/sessions/', { params }),
  getActivities: (params?: any) => api.get('/analytics/activities/', { params }),
  getPageViews: (params?: any) => api.get('/analytics/page_views/', { params }),
  getRealtime: () => api.get('/analytics/realtime/'),
  trackPageView: (data: { path: string; url?: string; title?: string }) =>
    api.post('/analytics/track/', data),
};

// Programs API
export const programsAPI = {
  getPrograms: (params?: any) => api.get('/programs/', { params }),
  getProgram: async (slug: string) => {
    const response = await api.get('/programs/', { params: { slug } });
    return {
      ...response,
      data: response.data?.results?.[0] || response.data?.[0] || null,
    };
  },
  getSessions: (params?: any) => api.get('/sessions/', { params }),
  getSession: (id: number) => api.get(`/sessions/${id}/`),
  getLevels: (params?: any) => api.get('/program-levels/', { params }),
  // Categories
  getCategories: () => api.get('/program-categories/'),
  createCategory: (data: any) => api.post('/program-categories/', data),
  updateCategory: (id: number, data: any) => api.patch(`/program-categories/${id}/`, data),
  deleteCategory: (id: number) => api.delete(`/program-categories/${id}/`),
};

// Registration API
export const registrationAPI = {
  getRegistrations: () => api.get('/registrations/registrations/'),
  createRegistration: (data: any) => api.post('/registrations/registrations/', data),
  getRegistration: (id: number) => api.get(`/registrations/registrations/${id}/`),
  cancelRegistration: (id: number, reason: string) =>
    api.post(`/registrations/registrations/${id}/cancel/`, { reason }),
  getWaitlist: () => api.get('/registrations/waitlist/'),
  joinWaitlist: (data: any) => api.post('/registrations/waitlist/', data),
};

// Children API
export const childrenAPI = {
  getChildren: () => api.get('/children/'),
  createChild: (data: any) => api.post('/children/', data),
  updateChild: (id: number, data: any) => api.patch(`/children/${id}/`, data),
  deleteChild: (id: number) => api.delete(`/children/${id}/`),
};

// Payment API
export const paymentAPI = {
  createPaymentIntent: (data: any) => api.post('/payments/create-payment-intent/', data),
  getPayments: () => api.get('/payments/payments/'),
  getReceipt: (paymentId: number) => api.get(`/payments/payments/${paymentId}/receipt/`),
  getInvoicePdf: (paymentId: number) => 
    api.get(`/payments/payments/${paymentId}/invoice_pdf/`, { responseType: 'blob' }),
  confirmPayment: (paymentId: number) => api.post(`/payments/payments/${paymentId}/confirm/`),
  refundPayment: (paymentId: number, data?: { amount?: number; reason?: string }) =>
    api.post(`/payments/payments/${paymentId}/refund/`, data || {}),
  validateCoupon: (code: string, amount: number) =>
    api.post('/payments/coupons/validate/', { code, amount }),
  // New cart checkout - creates children and registrations AFTER payment
  checkout: (data: {
    program_id: number;
    level_id?: number | null;
    children: any[];
    coupon_code?: string;
    heard_from?: string;
    payment_method_id?: string;
  }) => api.post('/payments/checkout/', data),
  // Validate coupon with subtotal
  validateCouponV2: (code: string, subtotal: number, guestEmail?: string) =>
    api.post('/payments/validate-coupon/', { code, subtotal, guest_email: guestEmail }),
};

// CMS API
export const cmsAPI = {
  getSettings: () => api.get('/cms/settings/'),
  updateSettings: (data: any) => api.patch('/cms/settings/', data),
  getPage: (slug: string) => api.get(`/cms/pages/${slug}/`),
  getPageData: (slug: string) => api.get(`/cms/page-data/${slug}/`),
  getMenu: (menu: string) => api.get('/cms/menu/', { params: { menu } }),
  getContentBlock: (identifier: string) => api.get(`/cms/content-blocks/${identifier}/`),
  getFeatures: () => api.get('/cms/features/'),
  getBanners: (position?: string) => api.get('/cms/banners/', { params: { position } }),
};

// Team API
export const teamAPI = {
  getTeam: () => api.get('/team/'),
};

// Testimonials API
export const testimonialsAPI = {
  getTestimonials: () => api.get('/testimonials/'),
  getFeatured: () => api.get('/testimonials/featured/'),
};

// FAQ API
export const faqAPI = {
  getFAQs: (category?: string) => api.get('/faqs/', { params: { category } }),
};

// Contact API
export const contactAPI = {
  submit: (data: any) => api.post('/contact/', data),
};

// Statistics API
export const statsAPI = {
  getStats: () => api.get('/statistics/'),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: () => api.get('/notifications/'),
  getUnreadCount: () => api.get('/notifications/unread_count/'),
  markAsRead: (id: number) => api.post(`/notifications/${id}/mark_read/`),
  markAllAsRead: () => api.post('/notifications/mark_all_read/'),
  deleteNotification: (id: number) => api.delete(`/notifications/${id}/`),
  clearAll: () => api.post('/notifications/clear_all/'),
  getPreferences: () => api.get('/notification-preferences/'),
  updatePreferences: (data: any) => api.patch('/notification-preferences/', data),
};

// Registrations API
export const registrationsAPI = {
  getRegistrations: () => api.get('/registrations/registrations/'),
  getRegistration: (id: number) => api.get(`/registrations/registrations/${id}/`),
  createRegistration: (data: any) => api.post('/registrations/registrations/', data),
  updateRegistration: (id: number, data: any) => api.patch(`/registrations/registrations/${id}/`, data),
  cancelRegistration: (id: number, reason: string) =>
    api.post(`/registrations/registrations/${id}/cancel/`, { cancellation_reason: reason }),
  // Level progression (admin)
  completeLesson: (id: number, data?: { lesson_number?: number; completed_date?: string; notes?: string }) =>
    api.post(`/registrations/registrations/${id}/complete_lesson/`, data || {}),
  undoLesson: (id: number, lessonNumber?: number) =>
    api.post(`/registrations/registrations/${id}/undo_lesson/`, lessonNumber ? { lesson_number: lessonNumber } : {}),
  evaluate: (id: number, data?: { notes?: string; next_program_id?: number }) =>
    api.post(`/registrations/registrations/${id}/evaluate/`, data || {}),
};

// Attendance Requests API
export const attendanceRequestAPI = {
  getMyRequests: () => api.get('/registrations/attendance-requests/my_requests/'),
  getAllRequests: () => api.get('/registrations/attendance-requests/'),
  getPendingRequests: () => api.get('/registrations/attendance-requests/pending/'),
  getRequest: (id: number) => api.get(`/registrations/attendance-requests/${id}/`),
  createRequest: (data: any) => api.post('/registrations/attendance-requests/', data),
  approveRequest: (id: number, adminResponse?: string) => 
    api.post(`/registrations/attendance-requests/${id}/approve/`, { admin_response: adminResponse }),
  denyRequest: (id: number, adminResponse: string) => 
    api.post(`/registrations/attendance-requests/${id}/deny/`, { admin_response: adminResponse }),
};

// Makeup Sessions API
export const makeupSessionsAPI = {
  getAll: () => api.get('/registrations/makeup-sessions/'),
  create: (data: any) => api.post('/registrations/makeup-sessions/', data),
  complete: (id: number) => api.post(`/registrations/makeup-sessions/${id}/complete/`),
  getEligibleChildren: (programId?: number) => 
    api.get('/registrations/makeup-sessions/eligible_children/', { params: programId ? { program_id: programId } : {} }),
};

// Attendance Billing API
export const attendanceBillingAPI = {
  getByProgram: (programId: number) => 
    api.get('/registrations/attendance/by_program/', { params: { program_id: programId } }),
  getSessionsAttended: (programId: number) => 
    api.get('/registrations/attendance/sessions_attended/', { params: { program_id: programId } }),
};

// Cart API
export const cartAPI = {
  getCart: () => api.get('/payments/cart/'),
  addItem: (data: { program_id: number; level_id?: number | null; child_data: any }) =>
    api.post('/payments/cart/add_item/', data),
  removeItem: (itemId: number) => api.post('/payments/cart/remove_item/', { item_id: itemId }),
  updateItem: (itemId: number, childData: any) =>
    api.post('/payments/cart/update_item/', { item_id: itemId, child_data: childData }),
  clearCart: () => api.post('/payments/cart/clear/'),
  getCount: () => api.get('/payments/cart/count/'),
  refreshTimer: () => api.post('/payments/cart/refresh_timer/'),
  checkout: (data: {
    coupon_code?: string;
    heard_from?: string;
    payment_method_id?: string;
    payment_intent_id?: string;
    setup_intent_id?: string;
    guest_contact?: { first_name: string; last_name: string; email: string; phone: string };
    terms_accepted?: boolean;
  }) =>
    api.post('/payments/cart/checkout/', data),
};

// Subscriptions API
export const subscriptionsAPI = {
  getAll: () => api.get('/payments/subscriptions/'),
  getBillingStatus: () => api.get('/payments/subscriptions/billing_status/'),
  getMySubscriptions: () => api.get('/payments/subscriptions/my_subscriptions/'),
  getSubscription: (id: number) => api.get(`/payments/subscriptions/${id}/`),
  createSubscription: (data: { child_id: number; program_id: number; level_id?: number; payment_method_id: string; test_mode?: boolean }) => 
    api.post('/payments/subscriptions/create_subscription/', data),
  cancelSubscription: (id: number, reason?: string) => 
    api.post(`/payments/subscriptions/${id}/cancel/`, { reason }),
  cancelMySubscription: (id: number, reason?: string) => 
    api.post(`/payments/subscriptions/${id}/cancel_my_subscription/`, { reason }),
  completeProgramBilling: (programId: number, levelId?: number) => 
    api.post('/payments/subscriptions/complete_program_billing/', { program_id: programId, level_id: levelId }),
  manualCharge: (subscriptionId: number) => 
    api.post(`/payments/subscriptions/${subscriptionId}/manual_charge/`),
  syncStripe: (subscriptionId: number) =>
    api.post(`/payments/subscriptions/${subscriptionId}/sync_stripe/`),
};

export default api;
